import { useEffect, useRef } from 'react';

const VIEWPORT_PADDING_PX = 8;
export const DISPATCHER_ORDER_HOVER_OVERLAY_CLASS = 'dispatcher-order-hover-overlay';
const SLIDE_MS = 220;

export const clearHoverOverlayStyles = (element: HTMLElement | null) => {
  if (!element) {
    return;
  }

  element.classList.remove(DISPATCHER_ORDER_HOVER_OVERLAY_CLASS);
  element.style.top = '';
  element.style.left = '';
  element.style.width = '';
  element.style.height = '';
  element.style.maxWidth = '';
  element.style.bottom = '';
  element.style.zIndex = '';
  element.style.overflow = '';
  element.style.transition = '';
};

const slideOverlayOpen = (element: HTMLElement, originRect: DOMRect) => {
  const maxWidth = Math.min(360, window.innerWidth - VIEWPORT_PADDING_PX * 2);
  const maxBottom = window.innerHeight - VIEWPORT_PADDING_PX;
  element.style.maxWidth = `${maxWidth}px`;
  const width = Math.max(originRect.width, Math.min(element.scrollWidth, maxWidth));
  element.style.width = `${width}px`;
  const fullHeight = element.scrollHeight;
  const originCenterY = originRect.top + originRect.height / 2;
  let top = originCenterY - fullHeight / 2;
  if (top < VIEWPORT_PADDING_PX) {
    top = VIEWPORT_PADDING_PX;
  }
  if (top + fullHeight > maxBottom) {
    top = Math.max(VIEWPORT_PADDING_PX, maxBottom - fullHeight);
  }

  void element.offsetHeight;
  element.style.transition = `height ${SLIDE_MS}ms ease-out, top ${SLIDE_MS}ms ease-out, width ${SLIDE_MS}ms ease-out`;
  element.style.width = `${width}px`;
  element.style.height = `${fullHeight}px`;
  element.style.top = `${top}px`;
  element.style.left = `${originRect.left}px`;
};

const enableHoverOverlay = (element: HTMLElement) => {
  if (!element.closest('.dispatcher-entry')) {
    return;
  }

  const rect = element.getBoundingClientRect();
  element.classList.add(DISPATCHER_ORDER_HOVER_OVERLAY_CLASS);
  element.style.transition = 'none';
  element.style.left = `${rect.left}px`;
  element.style.top = `${rect.top}px`;
  element.style.width = `${rect.width}px`;
  element.style.height = `${rect.height}px`;
  element.style.bottom = 'auto';
  element.style.overflow = 'hidden';
  element.style.zIndex = '6';

  requestAnimationFrame(() => {
    if (!element.classList.contains(DISPATCHER_ORDER_HOVER_OVERLAY_CLASS)) {
      return;
    }
    slideOverlayOpen(element, rect);
  });
};

export const useDispatcherOrderHoverOverlay = (enabled: boolean) => {
  const orderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const element = orderRef.current;
    if (!element) {
      return undefined;
    }

    const onViewportChange = () => clearHoverOverlayStyles(element);

    const onMouseEnter = () => {
      enableHoverOverlay(element);
      window.addEventListener('scroll', onViewportChange, true);
      window.addEventListener('resize', onViewportChange);
    };

    const onMouseLeave = () => {
      clearHoverOverlayStyles(element);
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
    };

    element.addEventListener('mouseenter', onMouseEnter);
    element.addEventListener('mouseleave', onMouseLeave);

    return () => {
      element.removeEventListener('mouseenter', onMouseEnter);
      element.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
      clearHoverOverlayStyles(element);
    };
  }, [enabled]);

  return orderRef;
};
